import React, { useEffect, useRef, useState, useCallback } from "react";
import { IconButton } from "../../../../ui/components/IconButton";
import { FeatherX, FeatherExternalLink, FeatherChevronRight } from "@subframe/core";
import {
  ExcelSheet,
  ExcelCell,
  getLedgerTransactions,
  getStoredCompanyId,
  TrialBalanceTransaction,
} from "../../data/api/trialBalanceApi";
import {
  cellStyleToCSS,
  formatWithNumberFormat,
  parseMergedCells,
  MergedCellInfo,
} from "../utils/excelStyleUtils";
import {
  getAllSheetReferences,
  SheetReference,
  parseTrialBalanceCriteria,
  TrialBalanceCriteria,
} from "../utils/formulaParser";
import { TrialBalanceTransactionsModal } from "./TrialBalanceTransactionsModal";

// Breadcrumb item for navigation trail
export interface BreadcrumbItem {
  sheet: string;
  cell: string;
}

interface SourceSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  sheetName: string;
  sheetData: ExcelSheet | undefined;
  targetCell: string;
  targetRow: number;
  targetCol: string;
  // Props for drill-down to register (new tab)
  parentLeadPath?: string; // e.g., "IS > Employee cost" - the parent context
  fromDate?: string; // Databook from date (e.g., "2025-04-01")
  toDate?: string; // Databook to date (e.g., "2025-06-30")
  // All sheets data for nested drill-down
  allSheets?: { [sheetName: string]: ExcelSheet };
  // Breadcrumb trail for navigation
  breadcrumbs?: BreadcrumbItem[];
}

// Nested source sheet context for recursive drill-down
interface NestedSourceContext {
  isOpen: boolean;
  sheet: string;
  cell: string;
  row: number;
  col: string;
}

interface TrialBalanceDrilldownState {
  isOpen: boolean;
  ledgerName: string;
  class1?: string;
  year: number;
  monthNumber: number;
  headerLabel: string;
}

// Rows that should NOT be clickable for drill-down
const NON_CLICKABLE_ROWS = [
  "total",
  "check",
  "check[]",
  "as per trial balance",
  "grand total",
  "sub total",
  "subtotal",
];

export const SourceSheetModal: React.FC<SourceSheetModalProps> = ({
  isOpen,
  onClose,
  sheetName,
  sheetData,
  targetCell,
  targetRow,
  targetCol,
  parentLeadPath,
  fromDate,
  toDate,
  allSheets,
  breadcrumbs = [],
}) => {
  const targetCellRef = useRef<HTMLTableCellElement>(null);
  
  // State for nested source sheet modal (recursive drill-down)
  const [nestedModal, setNestedModal] = useState<NestedSourceContext | null>(null);
  const [trialBalanceModal, setTrialBalanceModal] = useState<TrialBalanceDrilldownState | null>(null);
  const [trialBalanceLoading, setTrialBalanceLoading] = useState<boolean>(false);
  const [trialBalanceError, setTrialBalanceError] = useState<string>("");
  const [trialBalanceTransactions, setTrialBalanceTransactions] = useState<TrialBalanceTransaction[]>([]);
  const [trialBalanceOpeningBalance, setTrialBalanceOpeningBalance] = useState<number | null>(null);
  const [trialBalanceClosingBalance, setTrialBalanceClosingBalance] = useState<number | null>(null);
  const [trialBalanceMeta, setTrialBalanceMeta] = useState<{
    ledgerNameMatched?: string | null;
    zohoAccountId?: string | null;
  } | null>(null);
  
  // Current breadcrumb trail (add current sheet)
  const currentBreadcrumbs: BreadcrumbItem[] = [
    ...breadcrumbs,
    { sheet: sheetName, cell: targetCell }
  ];
  
  // Get last 3 breadcrumbs for display
  const displayBreadcrumbs = currentBreadcrumbs.slice(-3);
  const hasMoreBreadcrumbs = currentBreadcrumbs.length > 3;
  
  // Calculate how many levels to go back when clicking a breadcrumb
  const handleBreadcrumbClick = (index: number) => {
    // Index is relative to displayBreadcrumbs
    // If clicking on the last breadcrumb (current), do nothing
    const actualIndex = hasMoreBreadcrumbs 
      ? currentBreadcrumbs.length - 3 + index 
      : index;
    
    // Calculate how many levels to close
    const levelsToClose = currentBreadcrumbs.length - 1 - actualIndex;
    
    if (levelsToClose > 0) {
      // Close this modal (and any nested ones will be closed too)
      onClose();
    }
  };

  // Check if drill-down is enabled
  const isDrillDownEnabled = !!(parentLeadPath && fromDate && toDate);

  // Scroll to target cell when modal opens
  // Temporarily disabled — re-enable if/when we want the source sheet to
  // auto-center the referenced cell after open.
  // useEffect(() => {
  //   if (isOpen && targetCellRef.current) {
  //     // Small delay to ensure DOM is ready
  //     setTimeout(() => {
  //       targetCellRef.current?.scrollIntoView({
  //         behavior: "smooth",
  //         block: "center",
  //         inline: "center",
  //       });
  //     }, 100);
  //   }
  // }, [isOpen, targetRow, targetCol]);

  // Parse merged cells into lookup map
  const mergedCellsMap = React.useMemo((): Map<string, MergedCellInfo> => {
    return parseMergedCells(sheetData?.mergedCells);
  }, [sheetData?.mergedCells]);

  // Filter sheet data for display (hide column A, skip first 2 rows,
  // and cut off after "As per Trial Balance")
  const filteredSheet = React.useMemo(() => {
    if (!sheetData) return null;

    let cutoffRowIndex = sheetData.rows.length;
    for (let i = 0; i < sheetData.rows.length; i++) {
      const row = sheetData.rows[i];
      const cellA = row.find((cell) => cell.col === "A");
      if (cellA && String(cellA.value || "").trim().toLowerCase().includes("as per trial balance")) {
        cutoffRowIndex = i;
        break;
      }
    }

    const filteredRows = sheetData.rows
      .slice(2, cutoffRowIndex)
      .map((row) => ({
        originalRow: row,
        displayRow: row.filter((cell) => cell.col !== "A"),
      }));

    return {
      ...sheetData,
      rows: filteredRows,
    };
  }, [sheetData]);

  // Format cell value for display (respect Excel number formats)
  const formatCellValue = (cell: ExcelCell): string => {
    return formatWithNumberFormat(cell.value, cell.style?.numberFormat);
  };

  const getCellStyle = (
    cell: ExcelCell,
    isTarget: boolean,
    isGreenLink: boolean,
    isBlueLink: boolean,
    isTrialBalanceLink: boolean
  ): React.CSSProperties => {
    const style = { ...cellStyleToCSS(cell.style) };
    if (isTarget || isGreenLink || isBlueLink || isTrialBalanceLink) {
      delete style.backgroundColor;
      delete style.color;
      delete style.borderTop;
      delete style.borderBottom;
      delete style.borderLeft;
      delete style.borderRight;
    }
    return style;
  };

  // Check if this is the target cell
  const isTargetCell = (cell: ExcelCell): boolean => {
    return cell.row === targetRow && cell.col === targetCol;
  };

  // Get the row name from column A
  const getRowName = (row: ExcelCell[]): string | null => {
    const firstCell = row.find((cell) => cell.col === "A");
    if (firstCell && firstCell.value != null) {
      return String(firstCell.value).trim();
    }
    return null;
  };

  // Check if a row is clickable for drill-down
  const isRowClickable = (row: ExcelCell[]): boolean => {
    if (!isDrillDownEnabled) {
      return false;
    }

    const rowName = getRowName(row);
    if (!rowName) {
      return false;
    }

    // Check if row name is in non-clickable list
    const normalizedName = rowName.toLowerCase().trim();
    return !NON_CLICKABLE_ROWS.some(
      (nonClickable) =>
        normalizedName === nonClickable ||
        normalizedName.startsWith(nonClickable)
    );
  };

  // Get period columns and their headers (columns with date-like headers)
  const getPeriodColumns = (): { col: string; header: string }[] => {
    if (!sheetData || !sheetData.rows[0]) return [];
    
    const headerRow = sheetData.rows[0];
    const periodCols: { col: string; header: string }[] = [];
    
    // Period columns typically start from column C onwards and have date-like headers
    // e.g., "Apr - 2025", "May - 2025", etc.
    for (const cell of headerRow) {
      if (cell.col >= "C" && cell.value) {
        const headerValue = String(cell.value);
        // Check if it looks like a date header (contains month name or year)
        if (/[A-Za-z]+\s*-?\s*\d{4}/.test(headerValue) || 
            /jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec/i.test(headerValue)) {
          periodCols.push({ col: cell.col, header: headerValue });
        }
      }
    }
    
    return periodCols;
  };

  const getHeaderValueForColumn = (column: string): string | null => {
    if (!sheetData || !sheetData.rows[0]) return null;
    const headerRow = sheetData.rows[0];
    const headerCell = headerRow.find((cell) => cell.col === column);
    if (!headerCell?.value) {
      return null;
    }
    return String(headerCell.value).trim();
  };

  const parseMonthYearFromHeader = (
    header: string
  ): { year: number; monthNumber: number } | null => {
    const normalized = header.trim().toLowerCase();
    const match = normalized.match(
      /(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s*[- ]\s*(\d{4})/
    );
    if (!match) {
      return null;
    }

    const monthKey = match[1];
    const year = Number(match[2]);
    const monthMap: Record<string, number> = {
      jan: 1,
      january: 1,
      feb: 2,
      february: 2,
      mar: 3,
      march: 3,
      apr: 4,
      april: 4,
      may: 5,
      jun: 6,
      june: 6,
      jul: 7,
      july: 7,
      aug: 8,
      august: 8,
      sep: 9,
      september: 9,
      oct: 10,
      october: 10,
      nov: 11,
      november: 11,
      dec: 12,
      december: 12,
    };

    const monthNumber = monthMap[monthKey];
    if (!monthNumber || !year) {
      return null;
    }

    return { year, monthNumber };
  };

  // Extract period values from a row
  const extractPeriodValues = (row: ExcelCell[]): Record<string, number> => {
    const periodCols = getPeriodColumns();
    const values: Record<string, number> = {};
    
    for (const { col, header } of periodCols) {
      const cell = row.find(c => c.col === col);
      if (cell) {
        const numValue = typeof cell.value === 'number' 
          ? cell.value 
          : parseFloat(String(cell.value || '0').replace(/,/g, '')) || 0;
        values[header] = numValue;
      }
    }
    
    return values;
  };

  // Handle row click - open new tab with register view
  const handleRowClick = (row: ExcelCell[]) => {
    if (!isRowClickable(row) || !parentLeadPath || !fromDate || !toDate) {
      return;
    }

    const rowName = getRowName(row);
    if (!rowName) {
      return;
    }

    // Build the full lead path: parentLeadPath > rowName
    // e.g., "IS > Employee cost" + "Salaries" = "IS > Employee cost > Salaries"
    const fullLeadPath = `${parentLeadPath} > ${rowName}`;
    
    // Extract period values from the row
    const periodValues = extractPeriodValues(row);
    
    // Build URL for new tab
    const params = new URLSearchParams({
      lead: fullLeadPath,
      fromDate: fromDate,
      toDate: toDate,
    });
    
    // Add period values as base64 encoded JSON
    if (Object.keys(periodValues).length > 0) {
      const valuesJson = JSON.stringify(periodValues);
      params.append('periodValues', btoa(valuesJson));
    }
    
    const url = `/cfo/register-view?${params.toString()}`;
    
    // Open in new tab
    window.open(url, "_blank");
  };

  const handleTrialBalanceDrilldown = async (
    cell: ExcelCell,
    criteria: TrialBalanceCriteria
  ) => {
    const ledgerName = criteria.l2?.trim();
    console.debug("[TB Drilldown] Click", {
      sheet: sheetName,
      cell: `${cell.col}${cell.row}`,
      header: getHeaderValueForColumn(cell.col),
      class1: criteria.class1,
      l1: criteria.l1,
      l2: criteria.l2,
    });
    if (!ledgerName) {
      console.debug("[TB Drilldown] Missing L2 ledger name");
      return;
    }

    const headerValue = getHeaderValueForColumn(cell.col);
    if (!headerValue) {
      console.debug("[TB Drilldown] Missing header for column", cell.col);
      return;
    }

    const period = parseMonthYearFromHeader(headerValue);
    if (!period) {
      console.debug("[TB Drilldown] Failed to parse period from header", headerValue);
      return;
    }

    const companyId = getStoredCompanyId();
    setTrialBalanceModal({
      isOpen: true,
      ledgerName,
      class1: criteria.class1?.trim(),
      year: period.year,
      monthNumber: period.monthNumber,
      headerLabel: headerValue,
    });
    setTrialBalanceLoading(true);
    setTrialBalanceError("");
    setTrialBalanceTransactions([]);
    setTrialBalanceOpeningBalance(null);
    setTrialBalanceClosingBalance(null);
    setTrialBalanceMeta(null);

    try {
      console.debug("[TB Drilldown] Fetching transactions", {
        ledger_name: ledgerName,
        formula: cell.formula,
        class_1: criteria.class1?.trim(),
        year: period.year,
        month_number: period.monthNumber,
        company_id: companyId,
      });
      const response = await getLedgerTransactions({
        ledger_name: ledgerName,
        formula: cell.formula ?? undefined,
        year: period.year,
        month_number: period.monthNumber,
        class_1: criteria.class1?.trim(),
        company_id: companyId,
      });
      setTrialBalanceTransactions(response.data.transactions || []);
      setTrialBalanceOpeningBalance(
        response.data.opening_balance ?? null
      );
      setTrialBalanceClosingBalance(
        response.data.closing_balance ?? null
      );
      setTrialBalanceMeta({
        ledgerNameMatched: response.data.ledger_name_matched ?? response.data.ledger_name,
        zohoAccountId: response.data.zoho_account_id ?? null,
      });
    } catch (err: any) {
      console.debug("[TB Drilldown] Fetch error", err);
      setTrialBalanceError(err.message || "Failed to load transactions.");
    } finally {
      setTrialBalanceLoading(false);
    }
  };

  // Handle blue cell click - open nested source sheet modal
  const handleBlueCellClick = useCallback((cell: ExcelCell) => {
    if (!cell.formula || !allSheets) {
      return;
    }

    const refs = getAllSheetReferences(cell.formula);
    const ref = refs.find((r) => allSheets[r.sheet]);
    if (ref) {
      setNestedModal({
        isOpen: true,
        sheet: ref.sheet,
        cell: ref.cell,
        row: ref.row,
        col: ref.col,
      });
    }
  }, [allSheets]);

  // Check if a cell has a clickable sheet reference
  const hasCellSheetReference = (cell: ExcelCell): SheetReference | null => {
    if (!cell.formula || !allSheets) {
      return null;
    }
    const refs = getAllSheetReferences(cell.formula);
    const ref = refs.find((r) => allSheets[r.sheet]);
    return ref || null;
  };

  // Close nested modal
  const handleCloseNestedModal = () => {
    setNestedModal(null);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
      {/* Modal Container - Full Screen */}
      <div className="flex flex-col w-full h-full bg-default-background overflow-hidden">
        {/* Header with Breadcrumbs */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-border bg-brand-50">
          <div className="flex items-center gap-4">
            {/* Breadcrumb Navigation */}
            <div className="flex items-center gap-1">
              {hasMoreBreadcrumbs && (
                <span className="text-body font-body text-subtext-color">...</span>
              )}
              {hasMoreBreadcrumbs && (
                <FeatherChevronRight className="w-4 h-4 text-subtext-color" />
              )}
              {displayBreadcrumbs.map((crumb, index) => {
                const isLast = index === displayBreadcrumbs.length - 1;
                const isClickable = !isLast;
                
                return (
                  <div key={`${crumb.sheet}-${crumb.cell}-${index}`} className="flex items-center gap-1">
                    <button
                      onClick={() => isClickable && handleBreadcrumbClick(index)}
                      disabled={!isClickable}
                      className={`px-2 py-1 rounded text-body font-body transition-colors ${
                        isLast
                          ? "bg-brand-100 text-brand-700 font-bold cursor-default"
                          : "text-brand-600 hover:bg-brand-50 hover:text-brand-800 cursor-pointer underline"
                      }`}
                      title={isClickable ? `Go back to ${crumb.sheet}` : `Current: ${crumb.sheet}`}
                    >
                      {crumb.sheet}
                      <span className="text-caption ml-1 opacity-70">({crumb.cell})</span>
                    </button>
                    {!isLast && (
                      <FeatherChevronRight className="w-4 h-4 text-subtext-color" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <IconButton
            icon={<FeatherX />}
            onClick={onClose}
            size="medium"
          />
        </div>

        {/* Spreadsheet Grid */}
        <div className="flex-1 overflow-auto bg-white">
          {!filteredSheet ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-error-600">
                Sheet "{sheetName}" not found in the workbook
              </div>
            </div>
          ) : (
            <table className="border-collapse min-w-full">
              {/* Column widths */}
              {filteredSheet.columnWidths && (
                <colgroup>
                  <col style={{ width: "50px", minWidth: "50px" }} />
                  {filteredSheet.rows[0]?.displayRow?.map((cell) => (
                    <col
                      key={cell.col}
                      style={{
                        width: filteredSheet.columnWidths?.[cell.col]
                          ? `${filteredSheet.columnWidths[cell.col]}px`
                          : "120px",
                        minWidth: "60px",
                      }}
                    />
                  ))}
                </colgroup>
              )}
              {/* Column headers - sticky at top */}
              <thead className="sticky top-0 z-20">
                <tr>
                  <th className="sticky left-0 z-30 px-2 py-1 text-center text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                    #
                  </th>
                  {filteredSheet.rows[0]?.displayRow?.map((cell) => (
                    <th
                      key={cell.col}
                      className={`px-3 py-1 text-center text-caption font-caption border border-neutral-300 min-w-[120px] ${
                        cell.col === targetCol
                          ? "bg-brand-200 text-brand-800"
                          : "bg-neutral-200 text-subtext-color"
                      }`}
                    >
                      {cell.col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredSheet.rows.map(({ originalRow, displayRow }, rowIndex) => {
                  const clickable = isRowClickable(originalRow);
                  const rowName = getRowName(originalRow);
                  const rowNum = originalRow[0]?.row ?? displayRow[0]?.row ?? rowIndex + 1;
                  const rowHeight = filteredSheet.rowHeights?.[rowNum];
                  
                  return (
                    <tr
                      key={rowIndex}
                      style={rowHeight ? { height: `${rowHeight}px` } : undefined}
                      className={`
                        ${rowNum === targetRow ? "bg-brand-50" : ""}
                      `}
                    >
                      {/* Row number header */}
                      <td
                        className={`sticky left-0 z-10 px-2 py-1 text-center text-caption font-caption border border-neutral-200 min-w-[50px] ${
                          rowNum === targetRow
                            ? "bg-brand-200 text-brand-800 font-bold"
                            : "bg-neutral-100 text-subtext-color"
                        }`}
                      >
                        {rowNum}
                      </td>
                      {displayRow.map((cell, cellIndex) => {
                        // Make only column A (first data cell) clickable with green link
                        const isColumnA = cell.col === "A";
                        const showAsGreenLink = isColumnA && clickable;
                        
                        // Check if blue cell has a clickable sheet reference
                        const cellSheetRef = hasCellSheetReference(cell);
                        const isClickableBlueCell = !isColumnA && cellSheetRef !== null;

                        const trialBalanceCriteria = parseTrialBalanceCriteria(cell.formula);
                        const headerValue = !isColumnA ? getHeaderValueForColumn(cell.col) : null;
                        const periodInfo =
                          headerValue && trialBalanceCriteria
                            ? parseMonthYearFromHeader(headerValue)
                            : null;
                        const isTrialBalanceCell =
                          !isColumnA && !!trialBalanceCriteria?.l2 && !!periodInfo;

                        const cellAddress = `${cell.col}${cell.row}`;
                        const mergeInfo = mergedCellsMap.get(cellAddress);

                        // Skip hidden cells (covered by merge)
                        if (mergeInfo?.isHidden) {
                          return null;
                        }
                        
                        // Determine click handler
                        const handleClick = () => {
                          if (trialBalanceCriteria) {
                            console.debug("[TB Drilldown] Cell formula", cell.formula);
                          }
                          if (showAsGreenLink) {
                            handleRowClick(originalRow);
                          } else if (isTrialBalanceCell && trialBalanceCriteria) {
                            handleTrialBalanceDrilldown(cell, trialBalanceCriteria);
                          } else if (isClickableBlueCell) {
                            handleBlueCellClick(cell);
                          }
                        };
                        
                        return (
                          <td
                            key={`${cell.col}${cell.row}`}
                            ref={isTargetCell(cell) ? targetCellRef : undefined}
                            onClick={(showAsGreenLink || isClickableBlueCell || isTrialBalanceCell) ? handleClick : undefined}
                            colSpan={mergeInfo?.colSpan || 1}
                            rowSpan={mergeInfo?.rowSpan || 1}
                            className={`px-3 py-1 text-body font-body border min-w-[120px] max-w-[300px] truncate ${
                              isTargetCell(cell)
                                ? "bg-brand-500 text-white border-brand-700 border-2 font-bold"
                                : showAsGreenLink
                                ? "text-green-600 hover:text-green-800 hover:bg-green-50 cursor-pointer border-neutral-200 underline"
                                : isTrialBalanceCell
                                ? "bg-purple-50 hover:bg-purple-100 cursor-pointer border-neutral-200 text-purple-700"
                                : isClickableBlueCell
                                ? "bg-blue-100 hover:bg-blue-200 cursor-pointer border-neutral-200 text-blue-700"
                                : cell.formula
                                ? "bg-blue-50 border-neutral-200"
                                : "border-neutral-200"
                            }`}
                            style={getCellStyle(
                              cell,
                              isTargetCell(cell),
                              showAsGreenLink,
                              isClickableBlueCell,
                              isTrialBalanceCell
                            )}
                            title={
                              showAsGreenLink
                                ? `Click to view register for "${rowName}" (opens in new tab)`
                                : isTrialBalanceCell && trialBalanceCriteria
                                ? `Click to view transactions for "${trialBalanceCriteria.l2}" (${headerValue})\nFormula: ${cell.formula}`
                                : isClickableBlueCell
                                ? `Click to drill down to ${cellSheetRef!.sheet} (${cellSheetRef!.cell})\nFormula: ${cell.formula}`
                                : cell.formula
                                ? `Formula: ${cell.formula}\nValue: ${cell.value}`
                                : String(cell.value || "")
                            }
                          >
                            <div className="flex items-center gap-1">
                              {formatCellValue(cell)}
                              {showAsGreenLink && (
                                <FeatherExternalLink className="w-3 h-3 flex-shrink-0" />
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-neutral-border bg-neutral-50">
          <span className="text-caption font-caption text-subtext-color">
            {sheetData
              ? `${sheetData.maxRow} rows × ${sheetData.maxCol} columns`
              : ""}
          </span>
          <div className="flex items-center gap-4">
            {allSheets && (
              <span className="text-caption font-caption text-blue-600">
                Click blue cells to drill down to source sheets
              </span>
            )}
            {isDrillDownEnabled && (
              <span className="text-caption font-caption text-green-600">
                Click green links to view register (new tab)
              </span>
            )}
            <span className="text-caption font-caption text-brand-600">
              Target cell {targetCell} is highlighted
            </span>
          </div>
        </div>
      </div>

      {/* Nested Source Sheet Modal (recursive drill-down) */}
      {nestedModal && allSheets && (
        <SourceSheetModal
          isOpen={nestedModal.isOpen}
          onClose={handleCloseNestedModal}
          sheetName={nestedModal.sheet}
          sheetData={allSheets[nestedModal.sheet]}
          targetCell={nestedModal.cell}
          targetRow={nestedModal.row}
          targetCol={nestedModal.col}
          parentLeadPath={parentLeadPath ? `${parentLeadPath}` : sheetName}
          fromDate={fromDate}
          toDate={toDate}
          allSheets={allSheets}
          breadcrumbs={currentBreadcrumbs}
        />
      )}

      {trialBalanceModal && (
        <TrialBalanceTransactionsModal
          isOpen={trialBalanceModal.isOpen}
          onClose={() => setTrialBalanceModal(null)}
          title={`Transactions: ${trialBalanceModal.ledgerName}`}
          subtitle={[
            trialBalanceModal.headerLabel,
            trialBalanceModal.class1 ? `Class: ${trialBalanceModal.class1}` : null,
            trialBalanceMeta?.ledgerNameMatched &&
            trialBalanceMeta.ledgerNameMatched !== trialBalanceModal.ledgerName
              ? `Matched: ${trialBalanceMeta.ledgerNameMatched}`
              : null,
            trialBalanceMeta?.zohoAccountId
              ? `Zoho Account: ${trialBalanceMeta.zohoAccountId}`
              : null,
          ]
            .filter(Boolean)
            .join(" • ")}
          loading={trialBalanceLoading}
          error={trialBalanceError}
          transactions={trialBalanceTransactions}
          openingBalance={trialBalanceOpeningBalance}
          closingBalance={trialBalanceClosingBalance}
        />
      )}
    </div>
  );
};
